import { EventEmitter } from '@angular/core';
import { FormGroup } from '@angular/forms';
import type { Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import {
  createReactiveFormChange,
  createReactiveFormLifecycleEvent,
  createReactiveFormValueChange,
  type ReactiveFormChange,
  type ReactiveFormChangedField,
  type ReactiveFormLifecycleEvent,
  type ReactiveFormRawValueChange,
  type ReactiveFormStableValueChange,
  type ReactiveFormValue,
} from '../../interfaces/reactive-form-change.interface';
import { ReactiveFormValueUtils } from '../../model/reactive-form-value.utils';
import type { ReactiveChangeOrigin } from '../change-origin/reactive-change-origin';

export interface ReactiveFormEventEmitters<T> {
  readonly formChanges: EventEmitter<ReactiveFormChange>;
  readonly rawValueChanges: EventEmitter<ReactiveFormRawValueChange<T>>;
  readonly stableValueChanges: EventEmitter<ReactiveFormStableValueChange<T>>;
  readonly internalLifecycleEvents: EventEmitter<ReactiveFormLifecycleEvent<T>>;
}

export interface ReactiveFormValueChangeEvaluation<T> {
  readonly value: ReactiveFormValue<T>;
  readonly changedField: ReactiveFormChangedField<T> | null;
  readonly changedFields: readonly string[];
  readonly origin: ReactiveChangeOrigin;
  readonly shouldProcess: boolean;
}

export interface ReactiveFormValueChangeEvaluationOptions<T> {
  readonly id: string;
  readonly previousDataReady: boolean;
  readonly currentRawValue: ReactiveFormValue<T>;
}

/**
 * Owns value-change state and emissions for the Reactive Form Builder.
 *
 * This keeps Angular output wiring, previous snapshot tracking and origin propagation outside of the component so the
 * component can focus on rendering and business orchestration.
 */
export class ReactiveFormEventFacade<T> {
  private previousModel: ReactiveFormValue<T> = {};
  private nextModelChangeOrigin: ReactiveChangeOrigin | null = null;

  constructor(private readonly emitters: ReactiveFormEventEmitters<T>) {}

  public markNextModelChangeOrigin(origin: ReactiveChangeOrigin): void {
    this.nextModelChangeOrigin = origin;
  }

  public subscribeToModelChanges(
    model: FormGroup,
    destroy$: Observable<void>,
    handler: (newValuesRaw: unknown) => void | Promise<void>,
  ): void {
    model.valueChanges
      .pipe(
        debounceTime(0),
        distinctUntilChanged((previous, current) => ReactiveFormValueUtils.areModelSnapshotsEquivalent(
          previous as Record<string, unknown>,
          current as Record<string, unknown>,
        )),
        takeUntil(destroy$),
      )
      .subscribe((newValuesRaw: unknown) => {
        void handler(newValuesRaw);
      });
  }

  public evaluateValueChange(
    newValuesRaw: unknown,
    options: ReactiveFormValueChangeEvaluationOptions<T>,
  ): ReactiveFormValueChangeEvaluation<T> {
    const origin = this.consumeNextModelChangeOrigin();
    const newValues = newValuesRaw as ReactiveFormValue<T>;

    if (options.id && !options.previousDataReady) {
      this.syncPreviousModel(newValues);

      return this.createSkippedEvaluation(newValues, origin);
    }

    const changedFields = Object.keys(newValues).filter(
      (control) => !ReactiveFormValueUtils.areValuesEquivalent(newValues[control], this.previousModel?.[control]),
    );

    if (changedFields.length === 0) {
      this.syncPreviousModel(options.currentRawValue);

      return this.createSkippedEvaluation(newValues, origin);
    }

    return {
      value: newValues,
      changedField: changedFields[0] as ReactiveFormChangedField<T>,
      changedFields,
      origin,
      shouldProcess: true,
    };
  }

  public publishRawValueChange(
    value: ReactiveFormValue<T>,
    changedField: ReactiveFormChangedField<T>,
    changedFields: readonly string[],
    origin: ReactiveChangeOrigin,
  ): void {
    this.emitters.rawValueChanges.emit(createReactiveFormValueChange<T>(value, changedField, changedFields, origin));
  }

  public publishStableValueChange(
    value: ReactiveFormValue<T>,
    changedField: ReactiveFormChangedField<T>,
    changedFields: readonly string[],
    origin: ReactiveChangeOrigin,
  ): void {
    this.emitters.stableValueChanges.emit(createReactiveFormValueChange<T>(value, changedField, changedFields, origin));
    this.emitters.formChanges.emit(createReactiveFormChange<T>(value, changedField));
  }

  public publishStableSnapshot(
    currentModel: ReactiveFormValue<T>,
    changedField: string,
    origin: ReactiveChangeOrigin,
  ): void {
    const typedChangedField = changedField as ReactiveFormChangedField<T>;

    this.syncPreviousModel(currentModel);
    this.publishStableValueChange(currentModel, typedChangedField, [changedField], origin);
  }

  public publishSilentSnapshot(
    currentModel: ReactiveFormValue<T>,
    changedField: string,
    origin: ReactiveChangeOrigin,
  ): void {
    const typedChangedField = changedField as ReactiveFormChangedField<T>;

    this.syncPreviousModel(currentModel);
    this.emitters.internalLifecycleEvents.emit(createReactiveFormLifecycleEvent<T>(currentModel, typedChangedField, origin));
  }

  public syncPreviousModel(currentModel: ReactiveFormValue<T>): void {
    this.previousModel = { ...currentModel };
  }

  private consumeNextModelChangeOrigin(): ReactiveChangeOrigin {
    const origin = this.nextModelChangeOrigin ?? 'user';

    this.nextModelChangeOrigin = null;

    return origin;
  }

  private createSkippedEvaluation(
    value: ReactiveFormValue<T>,
    origin: ReactiveChangeOrigin,
  ): ReactiveFormValueChangeEvaluation<T> {
    return {
      value,
      changedField: null,
      changedFields: [],
      origin,
      shouldProcess: false,
    };
  }
}
